import { migrateIncidentTypeFilters } from "@/lib/utils/matters/incident-classification";
import {
  searchV4,
  emptySearchResponse,
  type SearchRequest,
} from "@/lib/data/requests/search/search";
import { type MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  type APIResponse,
  type MatterAssignee,
  type Connection,
} from "@/lib/types";

const DEFAULT_MATTER_STATUS_CATEGORIES = [
  "pre-litigation",
  "litigation",
] as const;
const DEFAULT_SEARCH_PAGE_SIZE = 50;

export type SearchMatterStatusCategory =
  (typeof MATTER_STATUS_CATEGORY_OPTIONS)[number];

export type MatterSearchResultStatus = {
  category: {
    id: string;
    label: string;
  };
  categoryId: string;
  id: string;
  label: string;
};

export type MatterSearchResultType = {
  incidentTypeCategoryId: string | null;
  id: string;
  label: string | null;
};

export type MatterSearchResultInsuranceCoverage = {
  contactName?: string | null;
  coverage?: string | null;
  insuranceId?: string;
};

export type MatterSearchResultInsuranceLiability = {
  contactName?: string | null;
  insuranceId?: string;
  liability?: string | null;
  liabilityPercentage?: string | number | null;
};

export type MatterSearchResultClientPhoneNumber = {
  extension?: string | null;
  number: string;
};

export type MatterSearchResult = {
  relatedMatters?: Array<{ id: string; title: string | null }> | null;
  aiAcknowledgement?: string | null;
  aiAffectedFields?: unknown[] | null;
  aiStatus?: string | null;
  attorneyFee?: string | number | null;
  assignees?: MatterAssignee[];
  clientInjuryDescription?: string | null;
  clients?: Array<{
    emailAddresses?: string[] | null;
    id: string;
    name?: string | null;
    /** String values are retained temporarily for the legacy API response. */
    phoneNumbers?: Array<MatterSearchResultClientPhoneNumber | string> | null;
    type?: string | null;
  }>;
  connections?: Connection[];
  criticalInfo?: string | null;
  dateCreated: string;
  dateModified: string;
  daysInStatus?: number | null;
  dateOfLoss?: string | null;
  defendantNames?: string[] | null;
  emailAddress?: string | null;
  expenseTotal?: string | number | null;
  firmId?: string | null;
  id: string;
  incidentAddressCity?: string | null;
  incidentAddressCountry?: string | null;
  incidentAddressCounty?: string | null;
  incidentAddressDetails?: string | null;
  incidentAddressState?: string | null;
  incidentAddressStreet1?: string | null;
  incidentAddressStreet2?: string | null;
  incidentAddressZip?: string | null;
  incidentDescription?: string | null;
  insuranceClaimNumbers?: string[] | null;
  insuranceCarriers?: string[] | null;
  insuranceCoverages?: MatterSearchResultInsuranceCoverage[] | null;
  insuranceLiabilities?: MatterSearchResultInsuranceLiability[] | null;
  insurancePolicyNumbers?: string[] | null;
  lastSeenDatetime?: string | null;
  lastTreatment?: string | null;
  lastTouchDatetime?: string | null;
  lastTouchMessage?: string | null;
  leadSourceId?: string | null;
  massTortId?: string | null;
  medicalTreatmentBillTotal?: string | number | null;
  nextTreatment?: string | null;
  recordsPending?: number | null;
  referredByNames?: string[] | null;
  referredToNames?: string[] | null;
  status?: MatterSearchResultStatus | null;
  statusId?: string | null;
  statusStartedAt?: string | null;
  statutesOfLimitations?: Array<{
    confirmed?: boolean;
    date: string;
    dateCreated: string;
    dateModified: string;
    description: string | null;
    firmId: string;
    id: string;
    matterId: string;
  }> | null;
  summary?: string | null;
  tags?: string[] | null;
  teams?: string[] | null;
  title?: string | null;
  incidentType?: MatterSearchResultType | null;
  incidentTypeId?: string | null;
  claimTypeIds?: string[];
  claimTypes?: Array<{ id: string; label: string }>;
};

export type SearchMattersInput = {
  massTortId?: string;
  matterIds?: string[];
  excludeMatterIds?: string[];
  context?: "files";
  clientDateOfBirthAfter?: string[] | string;
  clientDateOfBirthBefore?: string[] | string;
  clientDateOfBirthExact?: string[] | string;
  includeRelatedMatters?: boolean;
  clientEmail?: string[] | string;
  clientPhone?: string[] | string;
  contactName?: string[] | string;
  connectionLabel?: string[] | string;
  dateOfLossAfter?: string[] | string;
  dateOfLossBefore?: string[] | string;
  dateOfLossExact?: string[] | string;
  defendantName?: string[] | string;
  incidentAddress?: string[] | string;
  incidentAddressCity?: string[] | string;
  incidentAddressState?: string[] | string;
  insuranceCoverage?: string;
  insuranceClaimNumber?: string[] | string;
  insurancePolicyNumber?: string[] | string;
  liabilityCleared?: boolean;
  pageNumber?: number;
  pageSize?: number;
  referredByName?: string[] | string;
  referredToName?: string[] | string;
  order?: "asc" | "desc";
  search?: string[] | string;
  sort?: string;
  statusCategories?: SearchMatterStatusCategory[];
  statusId?: string[] | string;
  statusStartedFrom?: string[] | string;
  statusStartedTo?: string[] | string;
  tag?: string[] | string;
  team?: string[] | string;
  title?: string[] | string;
  incidentTypeId?: string[] | string;
  claimTypeIds?: string[] | string;
  unassigned?: boolean;
  userId?: string[] | string;
};

const firstValue = (value: string[] | string | undefined) => {
  if (Array.isArray(value)) {
    return value.find((item) => item.trim().length > 0);
  }
  return value?.trim() ? value : undefined;
};

const allValues = (value: string[] | string | undefined) => {
  const values = (Array.isArray(value) ? value : value ? [value] : [])
    .map((item) => item.trim())
    .filter(Boolean);
  return values.length ? values : undefined;
};

const joinedValues = (value: string[] | string | undefined) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => item.trim())
      .filter(Boolean)
      .join(" ");
  }
  return value?.trim() || undefined;
};

const getStatusCategories = (input: SearchMattersInput) =>
  input.statusCategories?.length
    ? input.statusCategories
    : input.matterIds
      ? undefined
      : [...DEFAULT_MATTER_STATUS_CATEGORIES];

const buildSearchMattersFilters = (input: SearchMattersInput) => ({
  massTortId: input.massTortId,
  matterIds: input.matterIds,
  excludeMatterIds: input.excludeMatterIds,
  defendantName: allValues(input.defendantName),
  clientDateOfBirthAfter: allValues(input.clientDateOfBirthAfter),
  clientDateOfBirthBefore: allValues(input.clientDateOfBirthBefore),
  clientDateOfBirthExact: allValues(input.clientDateOfBirthExact),
  includeRelatedMatters: input.includeRelatedMatters || undefined,
  clientEmail: allValues(input.clientEmail),
  clientPhone: allValues(input.clientPhone),
  contactName: allValues(input.contactName),
  connectionLabel:
    firstValue(input.connectionLabel) ??
    (allValues(input.contactName) ||
    allValues(input.clientEmail) ||
    allValues(input.clientPhone) ||
    allValues(input.clientDateOfBirthAfter) ||
    allValues(input.clientDateOfBirthBefore) ||
    allValues(input.clientDateOfBirthExact)
      ? "Client"
      : undefined),
  dateOfLossAfter: allValues(input.dateOfLossAfter),
  dateOfLossBefore: allValues(input.dateOfLossBefore),
  dateOfLossExact: allValues(input.dateOfLossExact),
  incidentAddress: joinedValues(input.incidentAddress),
  incidentAddressCity: allValues(input.incidentAddressCity),
  incidentAddressState: allValues(input.incidentAddressState),
  insuranceCoverage: input.insuranceCoverage,
  insuranceClaimNumber: allValues(input.insuranceClaimNumber),
  insurancePolicyNumber: allValues(input.insurancePolicyNumber),
  liabilityCleared: input.liabilityCleared,
  referredByName: allValues(input.referredByName),
  referredToName: allValues(input.referredToName),
  statusCategories: getStatusCategories(input),
  statusId: allValues(input.statusId),
  statusStartedFrom: firstValue(input.statusStartedFrom),
  statusStartedTo: firstValue(input.statusStartedTo),
  tag: allValues(input.tag),
  team: allValues(input.team),
  title: allValues(input.title),
  incidentTypeId: allValues(migrateIncidentTypeFilters(input).incidentTypeId),
  claimTypeIds: allValues(input.claimTypeIds),
  unassigned: input.unassigned || undefined,
  userId: allValues(input.userId),
});

type SearchMattersPagination = {
  count?: number;
  pageNumber?: number;
  pageSize?: number;
};

const getPagination = (response: APIResponse<MatterSearchResult[]>) =>
  response.pagination as SearchMattersPagination | undefined;

export const buildMatterSearchRequest = (
  input: SearchMattersInput,
  view: "list" | "count" = "list",
): SearchRequest => ({
  entityType: "matter",
  view,
  context: input.context,
  query: joinedValues(input.search),
  filters: buildSearchMattersFilters(input),
  ...(view === "list"
    ? {
        sort: migrateIncidentTypeFilters(input).sort,
        order: input.order,
        pageNumber: input.pageNumber,
        pageSize: input.pageSize ?? DEFAULT_SEARCH_PAGE_SIZE,
      }
    : {}),
});

export const searchMatters = (
  input: SearchMattersInput,
  signal?: AbortSignal,
  view: "list" | "count" = "list",
) => {
  if (input.matterIds?.length === 0)
    return Promise.resolve(
      emptySearchResponse<MatterSearchResult>(input.pageNumber, input.pageSize),
    );
  return searchV4<MatterSearchResult>(
    buildMatterSearchRequest(input, view),
    signal,
  );
};

export const searchAllMatters = async (
  input: SearchMattersInput,
  signal?: AbortSignal,
) => {
  const matters: MatterSearchResult[] = [];
  let pageNumber = input.pageNumber;
  let latestResponse: APIResponse<MatterSearchResult[]> = {
    data: [],
    meta: { userMsg: "" },
  };

  do {
    latestResponse = await searchMatters({ ...input, pageNumber }, signal);
    matters.push(...latestResponse.data);
    pageNumber = getSearchMattersNextPageNumber(latestResponse);
  } while (pageNumber);

  return {
    data: matters,
    meta: latestResponse.meta,
  } satisfies APIResponse<MatterSearchResult[]>;
};

export const getSearchMattersNextPageNumber = (
  response: APIResponse<MatterSearchResult[]> | undefined,
) => {
  if (!response) return undefined;
  const pagination = getPagination(response);
  const { count, pageNumber, pageSize } = pagination ?? {};
  if (count == null || pageNumber == null || pageSize == null) {
    return undefined;
  }
  const hasNextPage = pageNumber * pageSize < count;
  return hasNextPage ? pageNumber + 1 : undefined;
};

export const getSearchMattersCount = (
  response: APIResponse<MatterSearchResult[]> | undefined,
) => (response ? getPagination(response)?.count : undefined);
